import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import ko from 'dayjs/locale/ko';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { DefaultSeo } from 'next-seo';
import App, { AppContext } from 'next/app';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { RecoilRoot } from 'recoil';
import RecoilNexus from 'recoil-nexus';
import 'sanitize.css/sanitize.css';
import { ThemeProvider } from 'styled-components';
import 'swiper/css';
import 'swiper/css/pagination';
import 'doctornow-design-system/style.css';

import ErrorBoundary from '@/components/atom/ErrorBoundary';
import NaverAnalyticsScript from '@/components/common/NaverAnalyticsScript';
import RootErrorFallback from '@/components/common/RootErrorFallback';
import GlobalModal from '@/components/common/modal';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { IS_LOCAL } from '@/constants/env';
import useIsMounted from '@/hooks/useIsMounted';
import useLogger from '@/hooks/useLogger';
import GlobalStyle from '@/styles/globalStyle';
import '@/styles/globals.css';
import '@/styles/materialFont.css';
import FacebookPixel from '@/shared/ui/content-mark-down/facebook-pixel/FacebookPixel';
import theme from '@/styles/theme';
import { Client, CustomAppProps } from '@/types/app';
import { Nullable, ROUTES, isServer } from '@/types/common';
import Firebase from '@/utils/firebase';
import Hackle from '@/utils/hackle';
import Pixel from '@/utils/pixel/pixel';
import { initDatadogMonitoring } from '@/utils/report';
import { getUrlPathWithoutQueryString } from '@/utils/url';
import { ToastContainer } from 'doctornow-design-system';

import Toast from '@/components/common/toast';
import { getClientInfo } from '@/shared/utils/client-detector';
import { HeadlessDetector } from '@/shared/utils/detect';
import nextSeoConfig from '../../next-seo.config';

dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.locale(ko);

const isWebBrowserClient = (client: Client) => {
  return !client.isWebView && !isServer;
};

const QUERY_STALE_TIME = 2000;

export default function MyApp({ Component, pageProps: { dehydratedState, ...pageProps } }: CustomAppProps) {
  const route = useRouter();
  const isMounted = useIsMounted();
  const [prevPageName, setPrevPageName] = useState<Nullable<string>>();
  const isAirbridgeLink = route.query['airbridge_referrer'];
  const { userAgent, client } = pageProps;
  const logger = useLogger();

  if (isWebBrowserClient(client)) {
    if (HeadlessDetector.shouldInitializeSDK(window)) {
      Pixel.setup(client);
      Firebase.initApp(userAgent, client);
    }
  }

  const initializeRecoilState = () => {
    dayjs.extend(isBetween);
  };

  const queryClient = useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: 0,
          refetchOnWindowFocus: false,
          useErrorBoundary: false,
          refetchOnReconnect: false,
          staleTime: QUERY_STALE_TIME,
        },
        mutations: {
          retry: 0,
        },
      },
    })
  ).current;

  const Layout = Component.Layout ?? DefaultLayout;

  useEffect(() => {
    if (isMounted) {
      const currentPageName = getUrlPathWithoutQueryString(route.asPath);
      if (prevPageName !== currentPageName) {
        setPrevPageName(currentPageName);
        logger.pageView(userAgent);
      }
    }
  }, [isMounted, route, prevPageName, logger]);

  useEffect(() => {
    initDatadogMonitoring();
    Hackle.createHackle();
    if (IS_LOCAL) console.log('init', pageProps);
  }, [client.clientType]);

  useEffect(() => {
    if (isAirbridgeLink) {
      sessionStorage.setItem('airbridgeLink', JSON.stringify({ ...route.query }));
    }
  }, [isAirbridgeLink, route]);

  return (
    <>
      <DefaultSeo {...nextSeoConfig} canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${route.asPath}`} />

      {!client.isWebView && <FacebookPixel pixelId={process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID ?? ''} />}
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehydratedState}>
          <RecoilRoot initializeState={initializeRecoilState}>
            <RecoilNexus />
            <ThemeProvider theme={{ ...theme, client }}>
              <ToastContainer>
                <ErrorBoundary renderFallback={RootErrorFallback} onReset={() => route.replace(ROUTES.BOARD.HOME.path)}>
                  <GlobalStyle />
                  <GlobalModal />
                  <Toast userAgent={userAgent} />
                  <Toaster />
                  <NaverAnalyticsScript userAgent={userAgent} />
                  <Layout userAgent={userAgent} client={client}>
                    <Component {...pageProps} />
                  </Layout>
                </ErrorBoundary>
              </ToastContainer>
            </ThemeProvider>
          </RecoilRoot>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  // TODO: service_ 값들도 추후 Client 에 추가 필요 (로그 QA 필요하기에 해당 작업에서 제외됨)
  const appProps = await App.getInitialProps(appContext);
  const userAgent = (await appContext.ctx.req)
    ? (appContext.ctx.req?.headers['x-user-agent'] ?? appContext.ctx.req?.headers['user-agent'])
    : navigator.userAgent;

  appProps.pageProps.userAgent = userAgent;
  appProps.pageProps.client = getClientInfo(userAgent);

  return { ...appProps };
};
